import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isMeasured } from '../utils/handCalculations';
import { combine } from '../utils/amaMath';

export function calculateGripStrengthImpairment(gripData: {
  right_attempt_1: number | null;
  right_attempt_2: number | null;
  right_attempt_3: number | null;
  left_attempt_1: number | null;
  left_attempt_2: number | null;
  left_attempt_3: number | null;
}, side: 'right' | 'left'): number {
  // Return 0 if no grip data
  if (!gripData) return 0;

  // Map attempts based on tested vs non-tested side
  let testedAttempts: (number | null)[] = [];
  let nonTestedAttempts: (number | null)[] = [];

  if (side === 'right') {
    testedAttempts = [
      gripData.right_attempt_1,
      gripData.right_attempt_2,
      gripData.right_attempt_3
    ];
    nonTestedAttempts = [
      gripData.left_attempt_1,
      gripData.left_attempt_2,
      gripData.left_attempt_3
    ];
  } else { // side === 'left'
    testedAttempts = [
      gripData.left_attempt_1,
      gripData.left_attempt_2,
      gripData.left_attempt_3
    ];
    nonTestedAttempts = [
      gripData.right_attempt_1,
      gripData.right_attempt_2,
      gripData.right_attempt_3
    ];
  }

  // Helper function to calculate average, filtering out null/undefined values
  const safeAverage = (arr: (number | null)[]) => {
    const valid = arr.filter(val => val !== null && val !== undefined) as number[];
    if (valid.length === 0) return 0;
    return valid.reduce((sum, val) => sum + val, 0) / valid.length;
  };

  // Calculate averages
  const avgTested = safeAverage(testedAttempts);
  const avgNonTested = safeAverage(nonTestedAttempts);

  // Calculate decrease percentage
  const decreasePercent = avgNonTested > 0 ? Math.round((avgNonTested - avgTested) / avgNonTested * 100) : 0;

  // Rating based on decrease percentage according to Table 16-34, page 509 of AMA Guides
  let rating = 0;
  if (decreasePercent >= 61) rating = 30;
  else if (decreasePercent >= 31) rating = 20;
  else if (decreasePercent >= 10) rating = 10;

  return rating;
}

/**
 * 6. Add any special cases (like acromioplasty) using the combine function
 * 7. Implement conditional display in UI components - only show body region sections if at least one measurement
 *    exists (see implementation in the rating display sections, e.g., lines 2502-2528 for shoulder example)
 * 
 * EXAMPLE: Shoulder flexion calculation with anchor points from AMA Guides
 */

/**
 * UI COMPONENT DISPLAY PATTERNS
 * ===========================
 * 
 * Body Region Tab Styling Standard:
 * -------------------------------
 * Follow this standard styling for all body region tabs (shoulder example):
 * 
 * 1. Header Sizing & Hierarchy:
 *    - Main region header: text-2xl font-semibold mb-6 (e.g., "Shoulder")
 *    - Sub-section headers: text-lg font-medium (for both "Range of Motion" and "Rating" sections)
 * 
 * 2. Layout Structure:
 *    - Range of Motion section with bottom margin (mb-8)
 *    - ROM header with bottom border: border-b border-border pb-2 mb-4
 *    - Large gap between ROM section and Rating section (mt-16)
 *    - Rating header above divider line
 *    - Divider line (border-t border-border) below Rating header
 *    - Rating data display with padding (pt-4) below divider
 * 
 * Body Region Conditional Display Pattern:
 * ---------------------------------------
 * For each body region (e.g., shoulder, elbow, knee):
 * 
 * 1. In the WPI/rating calculation section:
 *    - Check if ALL measurements for the region are null
 *    - Return "--" if no measurements exist
 *    - Calculate and return rating if at least one measurement exists
 *    - Example: See right/left shoulder WPI calculation (lines ~2388-2460)
 * 
 * 2. In the narrative/detailed display section:
 *    - Only render the component if AT LEAST ONE measurement exists
 *    - Use logical OR (||) to check all relevant measurements
 *    - Wrap component in conditional rendering using && operator
 *    - Example: See ShoulderRatingNarrative rendering (lines ~2502-2528)
 * 
 * This ensures body regions without measurements don't appear in reports.
 */

export const calculateFlexion = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 180, imp: 0 },
    { deg: 170, imp: 1 },
    { deg: 160, imp: 1 },
    { deg: 150, imp: 2 },
    { deg: 140, imp: 3 },
    { deg: 130, imp: 3 },
    { deg: 120, imp: 4 },
    { deg: 110, imp: 5 },
    { deg: 100, imp: 5 },
    { deg: 90, imp: 6 },
    { deg: 80, imp: 7 },
    { deg: 70, imp: 7 },
    { deg: 60, imp: 8 },
    { deg: 50, imp: 9 },
    { deg: 40, imp: 10 },
    { deg: 30, imp: 10 },
    { deg: 20, imp: 11 },
    { deg: 10, imp: 16 },
    { deg: 0, imp: 21 },
  ];

  if (val >= 180) return 0;
  if (val <= 0) return 21;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];

    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateExtension = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 50, imp: 0 },
    { deg: 40, imp: 1 },
    { deg: 30, imp: 1 },
    { deg: 20, imp: 2 },
    { deg: 10, imp: 2 },
    { deg: 0, imp: 3 },
  ];

  if (val >= 50) return 0;
  if (val <= 0) return 3;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];

    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateAbduction = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 180, imp: 0 },
    { deg: 170, imp: 0 },
    { deg: 160, imp: 1 },
    { deg: 150, imp: 1 },
    { deg: 140, imp: 2 },
    { deg: 130, imp: 2 },
    { deg: 120, imp: 3 },
    { deg: 110, imp: 3 },
    { deg: 100, imp: 4 },
    { deg: 90, imp: 4 },
    { deg: 80, imp: 5 },
    { deg: 70, imp: 5 },
    { deg: 60, imp: 6 },
    { deg: 50, imp: 6 },
    { deg: 40, imp: 6 },
    { deg: 30, imp: 7 },
    { deg: 20, imp: 7 },
    { deg: 10, imp: 10 },
    { deg: 0, imp: 12 },
  ];

  if (val >= 180) return 0;
  if (val <= 0) return 12;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];

    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateAdduction = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 50, imp: 0 },
    { deg: 40, imp: 0 },
    { deg: 30, imp: 1 },
    { deg: 20, imp: 1 },
    { deg: 10, imp: 1 },
    { deg: 0, imp: 2 },
  ];

  if (val >= 50) return 0;
  if (val <= 0) return 2;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];

    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateInternalRotation = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 90, imp: 0 },
    { deg: 80, imp: 0 },
    { deg: 70, imp: 1 },
    { deg: 60, imp: 2 },
    { deg: 50, imp: 2 },
    { deg: 40, imp: 3 },
    { deg: 30, imp: 4 },
    { deg: 20, imp: 4 },
    { deg: 10, imp: 5 },
    { deg: 0, imp: 5 }
  ];

  if (val >= 90) return 0;
  if (val <= 0) return 5;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];

    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateExternalRotation = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 90, imp: 0 },
    { deg: 80, imp: 0 },
    { deg: 70, imp: 0 },
    { deg: 60, imp: 0 },
    { deg: 50, imp: 1 },
    { deg: 40, imp: 1 },
    { deg: 30, imp: 1 },
    { deg: 20, imp: 1 },
    { deg: 10, imp: 2 },
    { deg: 0, imp: 2 }
  ];

  if (val >= 90) return 0;
  if (val <= 0) return 2;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];

    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

// combine() imported from utils/amaMath (shared AMA Combined Values math)

// ShoulderRatingNarrative component
export interface ShoulderRatingNarrativeProps {
  shoulderData: {
    right_flexion?: number | null;
    left_flexion?: number | null;
    right_extension?: number | null;
    left_extension?: number | null;
    right_abduction?: number | null;
    left_abduction?: number | null;
    right_adduction?: number | null;
    left_adduction?: number | null;
    right_internal_rotation?: number | null;
    left_internal_rotation?: number | null;
    right_external_rotation?: number | null;
    left_external_rotation?: number | null;
  };
  side: "right" | "left";
  includeAcromioplasty: boolean | null;
}

export const ShoulderRatingNarrative: React.FC<ShoulderRatingNarrativeProps> = ({ shoulderData, side, includeAcromioplasty }) => {
  const [copied, setCopied] = useState(false);

  // Get relevant values based on side with additional safety checks
  // Using optional chaining to handle null/undefined shoulderData
  const flexion = shoulderData ? (side === "right" ? shoulderData.right_flexion : shoulderData.left_flexion) : null;
  const extension = shoulderData ? (side === "right" ? shoulderData.right_extension : shoulderData.left_extension) : null;
  const abduction = shoulderData ? (side === "right" ? shoulderData.right_abduction : shoulderData.left_abduction) : null;
  const adduction = shoulderData ? (side === "right" ? shoulderData.right_adduction : shoulderData.left_adduction) : null;
  const internalRotation = shoulderData ? (side === "right" ? shoulderData.right_internal_rotation : shoulderData.left_internal_rotation) : null;
  const externalRotation = shoulderData ? (side === "right" ? shoulderData.right_external_rotation : shoulderData.left_external_rotation) : null;
  
  // Calculate impairments - properly handle 0° values and undefined
  const flexionImpairment = calculateFlexion(flexion === 0 ? 0 : (flexion === undefined ? null : flexion));
  const extensionImpairment = calculateExtension(extension === 0 ? 0 : (extension === undefined ? null : extension));
  const abductionImpairment = calculateAbduction(abduction === 0 ? 0 : (abduction === undefined ? null : abduction));
  const adductionImpairment = calculateAdduction(adduction === 0 ? 0 : (adduction === undefined ? null : adduction));
  const irImpairment = calculateInternalRotation(internalRotation === 0 ? 0 : (internalRotation === undefined ? null : internalRotation));
  const erImpairment = calculateExternalRotation(externalRotation === 0 ? 0 : (externalRotation === undefined ? null : externalRotation));
  
  
  // Joint sum
  const joint_sum = flexionImpairment + extensionImpairment + abductionImpairment + adductionImpairment + irImpairment + erImpairment;
  
  // Handle acromioplasty if included
  let finalUeImpairment = joint_sum;
  let acromioplastyText = "";
  const acromioplastyValue = 10; // Value from Table 16-27
  
  if (includeAcromioplasty) {
    // Combine the joint_sum with acromioplastyValue
    finalUeImpairment = combine(joint_sum, acromioplastyValue);
    
    acromioplastyText = `\nThere was also an acromioplasty performed that is equated to a distal clavicle resection arthroplasty procedure. This procedure is combined with the range of motion (ROM) impairment using the method outlined on page 604. According to page 506, Table 16-27, the distal clavicle resection arthroplasty is worth 10% upper extremity impairment.\n\nCombining ${joint_sum}% (from ROM) with 10% (from distal clavicle resection arthroplasty procedure) upper extremity impairment = ${finalUeImpairment}% using the combined values chart on page 604.\n`;
  }
  
  // Calculate final WPI
  const ue_to_wpi = Math.round(finalUeImpairment * 0.6);
  
  // Build Narrative
  const shoulderTitle = side.charAt(0).toUpperCase() + side.slice(1) + " Shoulder";
  
  // Calculate the narrative text using useMemo to prevent unnecessary recalculations
  const narrative = useMemo(() => {
    // If shoulderData is null/undefined or if all values are null/undefined (but NOT 0), return null
    if (!shoulderData || 
        (flexion === null && extension === null && abduction === null && adduction === null && internalRotation === null && externalRotation === null) || 
        (flexion === undefined && extension === undefined && abduction === undefined && adduction === undefined && internalRotation === undefined && externalRotation === undefined)) {
      return null;
    }
    
    // Check if there's any actual impairment from calculations (not using finalUeImpairment)
    const hasImpairment = (flexionImpairment > 0 || extensionImpairment > 0 || abductionImpairment > 0 || 
                        adductionImpairment > 0 || irImpairment > 0 || erImpairment > 0);
    
    // Use appropriate intro based on whether there's actual impairment from calculations
    return `<b><u>${shoulderTitle}:</u></b>
${hasImpairment ? '\nImpairment rating due to range of motion deficit.\n\n' : 'No impairment based on range of motion.'}`
        + (flexion !== null ? `- Based on Figure 16-40 (page 476), the applicant has a ${flexionImpairment}% impairment of the upper extremity based on shoulder flexion to ${flexion}º as described in Figure 16-38 (page 475).\n\n` : '')
        + (extension !== null ? `- Based on Figure 16-40 (page 476), the applicant has a ${extensionImpairment}% impairment of the upper extremity based on shoulder extension to ${extension}º as described in Figure 16-38 (page 475).\n\n` : '')
        + (abduction !== null ? `- Based on Figure 16-43 (page 477), the applicant has a ${abductionImpairment}% impairment of the upper extremity based on abduction to ${abduction}º as described in Figure 16-38 (page 475).\n\n` : '')
        + (adduction !== null ? `- Based on Figure 16-43 (page 477), the applicant has a ${adductionImpairment}% impairment of the upper extremity based on adduction to ${adduction}º as described in Figure 16-38 (page 475).\n\n` : '')
        + (internalRotation !== null ? `- Based on Figure 16-46 (page 479), the applicant has a ${irImpairment}% impairment of the upper extremity based on internal rotation to ${internalRotation}º as described in Figure 16-44 (page 478).\n\n` : '')
        + (externalRotation !== null ? `- Based on Figure 16-46 (page 479), the applicant has a ${erImpairment}% impairment of the upper extremity based on external rotation to ${externalRotation || 0}º as described in Figure 16-44 (page 478).\n\n` : '')
        + `I will note that when range of motion measurements fall between standardized values, the corresponding impairment percentage is interpolated proportionally within the defined interval.\n\n`
        + `The upper extremity impairments are added for each range of motion deficit. The total is: ${joint_sum}% upper extremity impairment.${acromioplastyText}\n\n`
        + `The total is: ${finalUeImpairment}% upper extremity impairment converts to a ${ue_to_wpi}% whole person impairment using Table 16-3.\n\n`
        + `This level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;
  }, [shoulderData, flexion, extension, abduction, adduction, internalRotation, externalRotation, 
      flexionImpairment, extensionImpairment, abductionImpairment, adductionImpairment, 
      irImpairment, erImpairment, joint_sum, acromioplastyText, finalUeImpairment, ue_to_wpi, shoulderTitle]);
  
  const copyToClipboard = () => {
    if (narrative) {
      // Create a text node with the narrative
      const plainText = narrative.replace(/<\/?[^>]+(>|$)/g, '').replace(/\n{2,}/g, '\n'); // Remove HTML tags + collapse blank lines (single-spaced paste)
      navigator.clipboard.writeText(plainText)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    }
  };
  
  // If no narrative, don't render anything
  if (!narrative) return null;
  
  return (
    <div className="shoulder-rating-narrative mt-4 mb-6 bg-surface-overlay p-4 rounded-lg">
      <div 
        className="narrative-text" 
        dangerouslySetInnerHTML={{ __html: narrative }}
        style={{ fontFamily: 'Times New Roman', fontSize: '12pt' }}
      />
      <button 
        onClick={copyToClipboard} 
        className="copy-button mt-3 py-1 px-4 bg-accent-600 hover:bg-accent-700 text-white text-sm rounded"
      >
        {copied ? 'Copied!' : 'Copy Text (Times New Roman, 12pt)'}
      </button>
    </div>
  );
};

// Elbow ROM impairment calculation functions based on AMA 5th Edition
export const calculateElbowFlexion = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 140, imp: 0 },
    { deg: 130, imp: 1 },
    { deg: 120, imp: 2 },
    { deg: 110, imp: 4 },
    { deg: 100, imp: 6 },
    { deg: 90, imp: 8 },
    { deg: 80, imp: 10 },
    { deg: 70, imp: 15 },
    { deg: 60, imp: 19 },
    { deg: 50, imp: 23 },
    { deg: 40, imp: 27 },
    { deg: 30, imp: 31 },
    { deg: 20, imp: 34 },
    { deg: 10, imp: 37 },
    { deg: 0, imp: 42 }
  ];

  if (val >= 140) return 0;
  if (val <= 0) return 42;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateElbowExtension = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 0, imp: 0 },
    { deg: 10, imp: 1 },
    { deg: 20, imp: 2 },
    { deg: 30, imp: 3 },
    { deg: 40, imp: 4 },
    { deg: 50, imp: 5 },
    { deg: 60, imp: 6 },
    { deg: 70, imp: 8 },
    { deg: 80, imp: 11 },
    { deg: 90, imp: 17 },
    { deg: 100, imp: 21 },
    { deg: 110, imp: 27 },
    { deg: 120, imp: 32 },
    { deg: 130, imp: 37 },
    { deg: 140, imp: 42 }
  ];

  if (val <= 0) return 0;
  if (val >= 140) return 42;

  for (let i = 0; i < anchors.length - 1; i++) {
    const lower = anchors[i];
    const upper = anchors[i + 1];
    if (val >= lower.deg && val <= upper.deg) {
      const ratio = (val - lower.deg) / (upper.deg - lower.deg);
      const impairment = lower.imp + ratio * (upper.imp - lower.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculatePronation = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 80, imp: 0 },
    { deg: 70, imp: 1 },
    { deg: 60, imp: 1 },
    { deg: 50, imp: 2 },
    { deg: 40, imp: 3 },
    { deg: 30, imp: 3 },
    { deg: 20, imp: 4 },
    { deg: 10, imp: 8 },
    { deg: 0, imp: 12 }
  ];

  if (val >= 80) return 0;
  if (val <= 0) return 12;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateSupination = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 80, imp: 0 },
    { deg: 70, imp: 0 },
    { deg: 60, imp: 1 },
    { deg: 50, imp: 1 },
    { deg: 40, imp: 2 },
    { deg: 30, imp: 2 },
    { deg: 20, imp: 3 },
    { deg: 10, imp: 3 },
    { deg: 0, imp: 3 }
  ];

  if (val >= 80) return 0;
  if (val <= 0) return 3;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

// ElbowRatingNarrative component
export interface ElbowRatingNarrativeProps {
  elbowData: {
    right_flexion?: number | null;
    left_flexion?: number | null;
    right_extension?: number | null;
    left_extension?: number | null;
    right_pronation?: number | null;
    left_pronation?: number | null;
    right_supination?: number | null;
    left_supination?: number | null;
  };
  side: "right" | "left";
}

export const ElbowRatingNarrative: React.FC<ElbowRatingNarrativeProps> = ({ elbowData, side }) => {
  const [copied, setCopied] = useState(false);

  // Get relevant values based on side
  const flexion = side === 'right' ? elbowData?.right_flexion : elbowData?.left_flexion;
  const extension = side === 'right' ? elbowData?.right_extension : elbowData?.left_extension;
  const pronation = side === 'right' ? elbowData?.right_pronation : elbowData?.left_pronation;
  const supination = side === 'right' ? elbowData?.right_supination : elbowData?.left_supination;

  // Calculate impairments
  const flexionImpairment = calculateElbowFlexion(flexion === undefined ? null : flexion);
  const extensionImpairment = calculateElbowExtension(extension === undefined ? null : extension);
  const pronationImpairment = calculatePronation(pronation === undefined ? null : pronation);
  const supinationImpairment = calculateSupination(supination === undefined ? null : supination);

  // Joint sum
  const joint_sum = flexionImpairment + extensionImpairment + pronationImpairment + supinationImpairment;

  // Final UE impairment
  const finalUeImpairment = joint_sum;
  
  // Calculate final WPI
  const ue_to_wpi = Math.round(finalUeImpairment * 0.6);

  // Build Narrative
  const elbowTitle = side.charAt(0).toUpperCase() + side.slice(1) + " Elbow";

  const narrative = useMemo(() => {
    // If data is null/undefined or all values are null, return null
    if (!elbowData || (flexion === null && extension === null && pronation === null && supination === null)) {
      return null;
    }

    // Check if there's any impairment
    const hasImpairment = (flexionImpairment > 0 || extensionImpairment > 0 || pronationImpairment > 0 || supinationImpairment > 0);

    return `<b><u>${elbowTitle}:</u></b>
${hasImpairment ? '\nImpairment rating due to range of motion deficit.\n\n' : 'No impairment based on range of motion.'}`
      + (flexion !== null ? `- Based on Figure 16-34 (page 472), the applicant has a ${flexionImpairment}% impairment of the upper extremity based on elbow flexion to ${flexion}º.\n\n` : '')
      + (extension !== null ? `- Based on Figure 16-34 (page 472), the applicant has a ${extensionImpairment}% impairment of the upper extremity based on elbow extension to ${extension}º.\n\n` : '')
      + (pronation !== null ? `- Based on Figure 16-37 (page 474), the applicant has a ${pronationImpairment}% impairment of the upper extremity based on forearm pronation to ${pronation}º.\n\n` : '')
      + (supination !== null ? `- Based on Figure 16-37 (page 474), the applicant has a ${supinationImpairment}% impairment of the upper extremity based on forearm supination to ${supination}º.\n\n` : '')
      + `I will note that when range of motion measurements fall between standardized values, the corresponding impairment percentage is interpolated proportionally within the defined interval.\n\n`
      + `The upper extremity impairments are added for each range of motion deficit. The total is: ${joint_sum}% upper extremity impairment.\n\n`
      + `The total is: ${finalUeImpairment}% upper extremity impairment converts to a ${ue_to_wpi}% whole person impairment using Table 16-3.\n\n`
      + `This level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;
  }, [elbowData, flexion, extension, pronation, supination, flexionImpairment, extensionImpairment, pronationImpairment, supinationImpairment, joint_sum, finalUeImpairment, ue_to_wpi, elbowTitle]);

  const copyToClipboard = () => {
    if (narrative) {
      // Create a text node with the narrative
      const plainText = narrative.replace(/<\/?[^>]+(>|$)/g, '').replace(/\n{2,}/g, '\n'); // Remove HTML tags + collapse blank lines (single-spaced paste)
      navigator.clipboard.writeText(plainText)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    }
  };

  if (!narrative) return null;

  return (
    <div className="mt-4">
      <div className="mb-2 flex justify-between items-center">
        <h4 className="font-medium">Narrative</h4>
        <button 
          onClick={copyToClipboard}
          className="px-2 py-1 bg-accent-600 text-white text-xs rounded hover:bg-accent-700 transition-colors"
        >
          {copied ? 'Copied!' : 'Copy to Clipboard'}
        </button>
      </div>
      <div className="bg-surface-overlay p-3 rounded-lg border border-border">
        <div dangerouslySetInnerHTML={{ __html: narrative }} className="whitespace-pre-wrap text-sm" />
      </div>
    </div>
  );
};

// HipRatingNarrative component
export interface HipRatingNarrativeProps {
  hipData: {
    right_flexion?: number | null;
    left_flexion?: number | null;
    right_extension_contracture?: number | null;
    left_extension_contracture?: number | null;
    right_internal_rotation?: number | null;
    left_internal_rotation?: number | null;
    right_external_rotation?: number | null;
    left_external_rotation?: number | null;
    right_adduction?: number | null;
    left_adduction?: number | null;
    right_abduction_contracture?: number | null;
    left_abduction_contracture?: number | null;
  };
  side: "right" | "left";
}

export const HipRatingNarrative: React.FC<HipRatingNarrativeProps> = ({ hipData, side }) => {
  const [copied, setCopied] = useState(false);

  // Get relevant values based on side
  const flexion = side === 'right' ? hipData?.right_flexion : hipData?.left_flexion;
  const extensionContracture = side === 'right' ? hipData?.right_extension_contracture : hipData?.left_extension_contracture;
  const internalRotation = side === 'right' ? hipData?.right_internal_rotation : hipData?.left_internal_rotation;
  const externalRotation = side === 'right' ? hipData?.right_external_rotation : hipData?.left_external_rotation;
  const adduction = side === 'right' ? hipData?.right_adduction : hipData?.left_adduction;
  const abductionContracture = side === 'right' ? hipData?.right_abduction_contracture : hipData?.left_abduction_contracture;

  // Calculate impairments
  const flexionImpairment = calculateHipFlexion(flexion === undefined ? null : flexion);
  const extensionContractureImpairment = calculateHipExtensionContracture(extensionContracture === undefined ? null : extensionContracture);
  const internalRotationImpairment = calculateHipInternalRotation(internalRotation === undefined ? null : internalRotation);
  const externalRotationImpairment = calculateHipExternalRotation(externalRotation === undefined ? null : externalRotation);
  const adductionImpairment = calculateHipAdduction(adduction === undefined ? null : adduction);
  const abductionContractureImpairment = calculateHipAbductionContracture(abductionContracture === undefined ? null : abductionContracture);

  // Joint sum (Lower Extremity impairment)
  const joint_sum = flexionImpairment + extensionContractureImpairment + 
                   internalRotationImpairment + externalRotationImpairment + 
                   adductionImpairment + abductionContractureImpairment;

  // Final LE impairment
  const finalLeImpairment = joint_sum;
  
  // Calculate final WPI (40% of lower extremity rating)
  const le_to_wpi = Math.round(finalLeImpairment * 0.4);

  // Build Narrative
  const hipTitle = side.charAt(0).toUpperCase() + side.slice(1) + " Hip";

  const narrative = useMemo(() => {
    // If data is null/undefined or all values are null, return null
    if (!hipData || (flexion === null && extensionContracture === null && 
                    internalRotation === null && externalRotation === null && 
                    adduction === null && abductionContracture === null)) {
      return null;
    }

    // Check if there's any impairment
    const hasImpairment = (flexionImpairment > 0 || extensionContractureImpairment > 0 || 
                          internalRotationImpairment > 0 || externalRotationImpairment > 0 || 
                          adductionImpairment > 0 || abductionContractureImpairment > 0);

    return `<b><u>${hipTitle}:</u></b>
${hasImpairment ? '\nImpairment rating due to range of motion deficit.\n\n' : 'No impairment based on range of motion.'}`
      + (flexion !== null ? `- Based on Table 17-5 (page 537), the applicant has a ${flexionImpairment}% impairment of the lower extremity based on hip flexion to ${flexion}º.\n\n` : '')
      + (extensionContracture !== null ? `- Based on Table 17-5 (page 537), the applicant has a ${extensionContractureImpairment}% impairment of the lower extremity based on hip extension contracture of ${extensionContracture}º.\n\n` : '')
      + (internalRotation !== null ? `- Based on Table 17-5 (page 537), the applicant has a ${internalRotationImpairment}% impairment of the lower extremity based on hip internal rotation to ${internalRotation}º.\n\n` : '')
      + (externalRotation !== null ? `- Based on Table 17-5 (page 537), the applicant has a ${externalRotationImpairment}% impairment of the lower extremity based on hip external rotation to ${externalRotation}º.\n\n` : '')
      + (adduction !== null ? `- Based on Table 17-5 (page 537), the applicant has a ${adductionImpairment}% impairment of the lower extremity based on hip adduction to ${adduction}º.\n\n` : '')
      + (abductionContracture !== null ? `- Based on Table 17-5 (page 537), the applicant has a ${abductionContractureImpairment}% impairment of the lower extremity based on hip abduction contracture of ${abductionContracture}º.\n\n` : '')
      + `I will note that when range of motion measurements fall between standardized values, the corresponding impairment percentage is interpolated proportionally within the defined interval.\n\n`
      + `The lower extremity impairments are added for each range of motion deficit. The total is: ${joint_sum}% lower extremity impairment.\n\n`
      + `The total is: ${finalLeImpairment}% lower extremity impairment converts to a ${le_to_wpi}% whole person impairment using Table 17-3 (page 527).\n\n`
      + `This level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;
  }, [hipData, flexion, extensionContracture, internalRotation, externalRotation, adduction, abductionContracture,
      flexionImpairment, extensionContractureImpairment, internalRotationImpairment, externalRotationImpairment, 
      adductionImpairment, abductionContractureImpairment, joint_sum, finalLeImpairment, le_to_wpi, hipTitle]);

  const copyToClipboard = () => {
    if (narrative) {
      // Create a text node with the narrative
      const plainText = narrative.replace(/<\/?[^>]+(>|$)/g, '').replace(/\n{2,}/g, '\n'); // Remove HTML tags + collapse blank lines (single-spaced paste)
      navigator.clipboard.writeText(plainText)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    }
  };

  if (!narrative) return null;

  return (
    <div className="mt-4">
      <div className="mb-2 flex justify-between items-center">
        <h4 className="font-medium">Narrative</h4>
        <button 
          onClick={copyToClipboard}
          className="px-2 py-1 bg-accent-600 text-white text-xs rounded hover:bg-accent-700 transition-colors"
        >
          {copied ? 'Copied!' : 'Copy to Clipboard'}
        </button>
      </div>
      <div className="bg-surface-overlay p-3 rounded-lg border border-border">
        <div dangerouslySetInnerHTML={{ __html: narrative }} className="whitespace-pre-wrap text-sm" />
      </div>
    </div>
  );
};

// Wrist ROM impairment calculation functions based on AMA 5th Edition
export const calculateWristFlexion = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 60, imp: 0 },
    { deg: 50, imp: 2 },
    { deg: 40, imp: 3 },
    { deg: 30, imp: 5 },
    { deg: 20, imp: 7 },
    { deg: 10, imp: 8 },
    { deg: 0,  imp: 10 }
  ];
  
  if (val >= 60) return 0;
  if (val <= 0) return 10;
  
  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateWristExtension = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 60, imp: 0 },
    { deg: 50, imp: 2 },
    { deg: 40, imp: 4 },
    { deg: 30, imp: 5 },
    { deg: 20, imp: 7 },
    { deg: 10, imp: 8 },
    { deg: 0,  imp: 11 }
  ];

  if (val >= 60) return 0;
  if (val <= 0) return 11;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateWristRadialDeviation = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 20, imp: 0 },
    { deg: 15, imp: 1 },
    { deg: 10, imp: 2 },
    { deg: 5,  imp: 3 },
    { deg: 0,  imp: 4 }
  ];

  if (val >= 20) return 0;
  if (val <= 0) return 4;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

export const calculateWristUlnarDeviation = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  
  const anchors = [
    { deg: 30, imp: 0 },
    { deg: 25, imp: 1 },
    { deg: 20, imp: 2 },
    { deg: 15, imp: 3 },
    { deg: 10, imp: 4 },
    { deg: 5,  imp: 4 },
    { deg: 0,  imp: 5 }
  ];

  if (val >= 30) return 0;
  if (val <= 0) return 5;

  for (let i = 0; i < anchors.length - 1; i++) {
    const upper = anchors[i];
    const lower = anchors[i + 1];
    if (val <= upper.deg && val >= lower.deg) {
      const ratio = (upper.deg - val) / (upper.deg - lower.deg);
      const impairment = upper.imp + ratio * (lower.imp - upper.imp);
      return Math.round(impairment);
    }
  }
  return 0;
};

// WristRatingNarrative component
export interface WristRatingNarrativeProps {
  wristData: {
    right_flexion?: number | null;
    left_flexion?: number | null;
    right_extension?: number | null;
    left_extension?: number | null;
    right_radial_deviation?: number | null;
    left_radial_deviation?: number | null;
    right_ulnar_deviation?: number | null;
    left_ulnar_deviation?: number | null;
  };
  side: "right" | "left";
}

export const WristRatingNarrative: React.FC<WristRatingNarrativeProps> = ({ wristData, side }) => {
  const [copied, setCopied] = useState(false);

  // Get relevant values based on side with additional safety checks
  const flexion = wristData ? (side === "right" ? wristData.right_flexion : wristData.left_flexion) : null;
  const extension = wristData ? (side === "right" ? wristData.right_extension : wristData.left_extension) : null;
  const radialDeviation = wristData ? (side === "right" ? wristData.right_radial_deviation : wristData.left_radial_deviation) : null;
  const ulnarDeviation = wristData ? (side === "right" ? wristData.right_ulnar_deviation : wristData.left_ulnar_deviation) : null;
  
  // Calculate impairments - properly handle 0° values and undefined
  const flexionImpairment = calculateWristFlexion(flexion === 0 ? 0 : (flexion === undefined ? null : flexion));
  const extensionImpairment = calculateWristExtension(extension === 0 ? 0 : (extension === undefined ? null : extension));
  const radialDeviationImpairment = calculateWristRadialDeviation(radialDeviation === 0 ? 0 : (radialDeviation === undefined ? null : radialDeviation));
  const ulnarDeviationImpairment = calculateWristUlnarDeviation(ulnarDeviation === 0 ? 0 : (ulnarDeviation === undefined ? null : ulnarDeviation));
  
  // Calculate joint sum and convert to WPI
  const joint_sum = flexionImpairment + extensionImpairment + radialDeviationImpairment + ulnarDeviationImpairment;
  const ue_to_wpi = Math.round(joint_sum * 0.6);
  
  // Build Narrative
  const wristTitle = side.charAt(0).toUpperCase() + side.slice(1) + " Wrist";
  
  // Calculate the narrative text using useMemo to prevent unnecessary recalculations
  const narrative = useMemo(() => {
    // If wristData is null/undefined or if all values are null/undefined (but NOT 0), return null
    if (!wristData || 
        (flexion === null && extension === null && radialDeviation === null && ulnarDeviation === null) || 
        (flexion === undefined && extension === undefined && radialDeviation === undefined && ulnarDeviation === undefined)) {
      return null;
    }
    
    // Check if there's any actual impairment from calculations
    const hasImpairment = (flexionImpairment > 0 || extensionImpairment > 0 || radialDeviationImpairment > 0 || ulnarDeviationImpairment > 0);
    
    return `<b><u>${wristTitle}:</u></b>
${hasImpairment ? '\nImpairment rating due to range of motion deficit.\n\n' : 'No impairment based on range of motion.'}`
        + (flexion !== null ? `- Based on Figure 16-28 (page 467), the applicant has ${flexionImpairment}% upper extremity impairment based on flexion (palmar-flexion) of ${flexion}º as described in Figure 16-26 (page 466).\n\n` : '')
        + (extension !== null ? `- Based on Figure 16-28 (page 467), the applicant has ${extensionImpairment}% upper extremity impairment based on extension (dorsiflexion) of ${extension}º as described in Figure 16-26 (page 466).\n\n` : '')
        + (radialDeviation !== null ? `- Based on Figure 16-31 (page 469), the applicant has ${radialDeviationImpairment}% upper extremity impairment based on radial deviation of ${radialDeviation}º as described in Figure 16-29 (page 468).\n\n` : '')
        + (ulnarDeviation !== null ? `- Based on Figure 16-31 (page 469), the applicant has ${ulnarDeviationImpairment}% upper extremity impairment based on ulnar deviation of ${ulnarDeviation}º as described in Figure 16-29 (page 468).\n\n` : '')
        + `I will note that when range of motion measurements fall between standardized values, the corresponding impairment percentage is interpolated proportionally within the defined interval.\n\n`
        + `The range of motion impairments to the wrist are added, according to the procedures on pages 466 and 468 of the AMA Guides 5th Edition. Impairment of the upper extremity due to abnormal wrist motion is ${joint_sum}%.\n\n`
        + `Per Table 16-3 (page 439), ${joint_sum}% upper extremity impairment converts to ${ue_to_wpi}% WPI.\n\n`
        + `This level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;
  }, [wristData, flexion, extension, radialDeviation, ulnarDeviation, 
      flexionImpairment, extensionImpairment, radialDeviationImpairment, ulnarDeviationImpairment,
      joint_sum, ue_to_wpi, wristTitle]);
  
  const copyToClipboard = () => {
    if (narrative) {
      // Create a text node with the narrative
      const plainText = narrative.replace(/<\/?[^>]+(>|$)/g, '').replace(/\n{2,}/g, '\n'); // Remove HTML tags + collapse blank lines (single-spaced paste)
      navigator.clipboard.writeText(plainText)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    }
  };
  
  // If no narrative, don't render anything
  if (!narrative) return null;
  
  return (
    <div className="wrist-rating-narrative mt-4 mb-6 bg-surface-overlay p-4 rounded-lg">
      <div 
        className="narrative-text" 
        dangerouslySetInnerHTML={{ __html: narrative }}
        style={{ fontFamily: 'Times New Roman', fontSize: '12pt' }}
      />
      <button 
        onClick={copyToClipboard} 
        className="copy-button mt-3 py-1 px-4 bg-accent-600 hover:bg-accent-700 text-white text-sm rounded"
      >
        {copied ? 'Copied!' : 'Copy Text (Times New Roman, 12pt)'}
      </button>
    </div>
  );
};

// GripRatingNarrative component
export interface GripRatingNarrativeProps {
  gripData: {
    right_attempt_1: number | null;
    left_attempt_1: number | null;
    right_attempt_2: number | null;
    left_attempt_2: number | null;
    right_attempt_3: number | null;
    left_attempt_3: number | null;
  };
  side: "right" | "left";
}

export const GripRatingNarrative: React.FC<GripRatingNarrativeProps> = ({ gripData, side }) => {
  const [copied, setCopied] = useState(false);

  // Get relevant grip strength values based on side
  const attempt1 = gripData ? (side === "right" ? gripData.right_attempt_1 : gripData.left_attempt_1) : null;
  const attempt2 = gripData ? (side === "right" ? gripData.right_attempt_2 : gripData.left_attempt_2) : null;
  const attempt3 = gripData ? (side === "right" ? gripData.right_attempt_3 : gripData.left_attempt_3) : null;

  // Get the non-tested side values
  const nonTestedAttempt1 = gripData ? (side === "right" ? gripData.left_attempt_1 : gripData.right_attempt_1) : null;
  const nonTestedAttempt2 = gripData ? (side === "right" ? gripData.left_attempt_2 : gripData.right_attempt_2) : null;
  const nonTestedAttempt3 = gripData ? (side === "right" ? gripData.left_attempt_3 : gripData.right_attempt_3) : null;

  // Helper function to safely calculate average of non-null grip values
  const calculateAverage = (values: (number | null)[]): number => {
    const validValues = values.filter(val => val !== null && val !== undefined) as number[];
    if (validValues.length === 0) return 0;
    return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
  };

  // Calculate averages
  const testedAverage = calculateAverage([attempt1, attempt2, attempt3]);
  const nonTestedAverage = calculateAverage([nonTestedAttempt1, nonTestedAttempt2, nonTestedAttempt3]);

  // Calculate percentage decrease
  const decreasePercent = nonTestedAverage > 0 ? 
    Math.round((nonTestedAverage - testedAverage) / nonTestedAverage * 100) : 0;
  
  // Calculate UE impairment
  const ueImpairment = calculateGripStrengthImpairment(gripData, side);
  
  // Convert to WPI
  const wpiImpairment = Math.round(ueImpairment * 0.6);

  // Build narrative
  const gripTitle = side.charAt(0).toUpperCase() + side.slice(1) + " Grip Strength";

  // Calculate the narrative text using useMemo to prevent unnecessary recalculations
  const narrative = useMemo(() => {
    // If gripData is null/undefined or if all attempts are null/undefined, return null
    if (!gripData || 
        (attempt1 === null && attempt2 === null && attempt3 === null) || 
        (attempt1 === undefined && attempt2 === undefined && attempt3 === undefined)) {
      return null;
    }
    
    // Check if there's any actual impairment from calculations
    const hasImpairment = ueImpairment > 0;
    
    return `<b><u>${gripTitle}:</u></b>
${hasImpairment ? '\nImpairment rating due to grip strength deficit.\n\n' : 'No impairment based on grip strength.'}` +
    (hasImpairment ? `The applicant's ${side} side grip strength measurements were ${attempt1 !== null ? attempt1 : 'not measured'}, ${attempt2 !== null ? attempt2 : 'not measured'}, and ${attempt3 !== null ? attempt3 : 'not measured'}, with an average of ${testedAverage.toFixed(1)}.\n\n` : '') +
    (hasImpairment ? `The non-tested side (${side === 'right' ? 'left' : 'right'}) grip strength measurements were ${nonTestedAttempt1 !== null ? nonTestedAttempt1 : 'not measured'}, ${nonTestedAttempt2 !== null ? nonTestedAttempt2 : 'not measured'}, and ${nonTestedAttempt3 !== null ? nonTestedAttempt3 : 'not measured'}, with an average of ${nonTestedAverage.toFixed(1)}.\n\n` : '') +
    (hasImpairment ? `The ${side} side grip strength shows a ${decreasePercent}% decrease compared to the ${side === 'right' ? 'left' : 'right'} side.\n\n` : '') +
    (hasImpairment ? `Based on Table 16-34 (page 509), this corresponds to a ${ueImpairment}% upper extremity impairment.\n\n` : '') +
    (hasImpairment ? `Per Table 16-3 (page 439), ${ueImpairment}% upper extremity impairment converts to ${wpiImpairment}% WPI.\n\n` : '') +
    `${hasImpairment ? 'This level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.' : ''}`;
  }, [gripData, attempt1, attempt2, attempt3, nonTestedAttempt1, nonTestedAttempt2, nonTestedAttempt3,
      testedAverage, nonTestedAverage, decreasePercent, ueImpairment, wpiImpairment, gripTitle]);
  
  const copyToClipboard = () => {
    if (narrative) {
      // Create a text node with the narrative
      const plainText = narrative.replace(/<\/?[^>]+(>|$)/g, '').replace(/\n{2,}/g, '\n'); // Remove HTML tags + collapse blank lines (single-spaced paste)
      navigator.clipboard.writeText(plainText)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy text: ', err);
        });
    }
  };
  
  // If no narrative, don't render anything
  if (!narrative) return null;
  
  return (
    <div className="grip-rating-narrative mt-4 mb-6 bg-surface-overlay p-4 rounded-lg">
      <div 
        className="narrative-text" 
        dangerouslySetInnerHTML={{ __html: narrative }}
        style={{ fontFamily: 'Times New Roman', fontSize: '12pt' }}
      />
      <button 
        onClick={copyToClipboard} 
        className="copy-button mt-3 py-1 px-4 bg-accent-600 hover:bg-accent-700 text-white text-sm rounded"
      >
        {copied ? 'Copied!' : 'Copy Text'}
      </button>
    </div>
  );
};

// Hip ROM impairment — AMA Guides 5th Ed, Chapter 17 (Table 17-9), in % LOWER
// EXTREMITY. The caller sums the motions and converts LE → WPI (x 0.4).
//
// Ch.17 rates the hip in DISCRETE severity bands, not on a smooth ramp — a hip
// that flexes to 79 degrees and one that flexes to 51 both score 10% LE. These
// tables are ported from the QME - Tool reference calculator
// (src/lib/ratings/calculators/hip.ts), which is the trusted production source.
// The previous implementation here interpolated linearly between invented
// anchors and under-rated every impaired hip (e.g. 30 degrees of flexion scored
// 7% LE instead of 20%).
export const calculateHipFlexion = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val < 50) return 20;
  if (val < 80) return 10;
  if (val < 100) return 5;
  return 0;
};

export const calculateHipExtensionContracture = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val >= 30) return 20;
  if (val >= 20) return 10;
  if (val >= 10) return 5;
  return 0;
};

export const calculateHipInternalRotation = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val >= 10 && val <= 20) return 5;
  if (val >= 0 && val < 10) return 10;
  return 0;
};

export const calculateHipExternalRotation = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val >= 20 && val <= 30) return 5;
  if (val >= 0 && val < 20) return 10;
  return 0;
};

export const calculateHipAdduction = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val >= 0 && val <= 15) return 5;
  return 0;
};

// Deviation from the reference, deliberate: the reference returns 5 for ANY
// value >= 0, because in that app a hip with no contracture leaves the field
// ABSENT rather than 0. This app fills 0 to mean "no contracture", so 0 must
// score 0 — otherwise every normal hip picks up 5% LE (2% WPI). Every genuine
// contracture (> 0) uses the reference's Table 17-9 bands unchanged.
export const calculateHipAbductionContracture = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val <= 0) return 0;
  if (val > 20) return 38;
  if (val >= 11) return 20;
  if (val >= 6) return 10;
  return 5;
};

// ===========================================================================
// Knee ROM impairment — AMA Guides 5th Ed, Chapter 17, in % LOWER EXTREMITY.
// Callers sum the motions and convert LE → WPI (x 0.4). Ported from the
// QME - Tool reference (src/lib/ratings/calculators/knee.ts); previously these
// lived inline and duplicated inside KneesTab for each side.
// ===========================================================================

// AMA Table 17-33 caps angular-deformity additions at a MAX of 50% LE (20% WPI).
// Without the cap the "+2% per 2 degrees" term grows without bound and produces
// impossible values above 100%.
const ANGULAR_MAX_LE = 50;

export const calculateKneeFlexion = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val >= 110) return 0;
  if (val >= 80) return 10;
  if (val >= 60) return 20;
  return 35 + Math.floor((60 - val) / 10) * 2;
};

export const calculateKneeFlexionContracture = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val >= 20) return 35;
  if (val >= 10) return 20;
  if (val >= 5) return 10;
  return 0;
};

// AMA Table 17-10: 1-7 degrees varus = Moderate (20% LE), 8-12 = Severe (35% LE),
// above 12 adds 2% LE per 2 degrees. The bands used to sit one severity tier low
// (1-7 -> 10, 8-12 -> 20), which under-rated every varus knee against the book.
export const calculateKneeVarus = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val > 12) return Math.min(ANGULAR_MAX_LE, 35 + Math.floor((val - 12) / 2) * 2);
  if (val >= 8) return 35;
  if (val >= 1) return 20;
  return 0;
};

export const calculateKneeValgus = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val > 20) return Math.min(ANGULAR_MAX_LE, 35 + Math.floor((val - 20) / 2) * 2);
  if (val >= 16) return 35;
  if (val >= 13) return 20;
  if (val >= 10) return 10;
  return 0;
};

// ===========================================================================
// Ankle ROM impairment — AMA Guides 5th Ed, Chapter 17, in % LOWER EXTREMITY.
// Same sum-then-x0.4 chain as the knee and hip.
// ===========================================================================

export const calculateAnklePlantarFlexion = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val >= 21) return 0;
  if (val >= 11) return 7;
  if (val >= 1) return 15;
  return 30;
};

export const calculateAnkleDorsiflexion = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val <= -20) return 30;
  if (val <= -10) return 15;
  if (val <= 10) return 7;
  return 0;
};

export const calculateAnkleInversion = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val > 20) return 0;
  if (val > 9) return 2;
  return 5;
};

export const calculateAnkleEversion = (val: number | null): number => {
  if (!isMeasured(val)) return 0;
  if (val > 10) return 0;
  return 2;
};

// ThumbValue extraction function
